import PropTypes from 'prop-types';

// Caching filtered dropdown options for improved performance.
const dropdownCache = {};

/**
 * Filters dropdown options based on the dropdown name and caches the result to avoid redundant filtering.
 * @param {string} dropdownName - The name of the dropdown to filter.
 * @param {Array} apiResponse - The API response containing all dropdown items.
 * @returns {Array} - A list of filtered dropdown options.
 */
const filterDropDownList = (dropdownName, apiResponse) => {
    // Check if the dropdown options for this name are already cached
    if (!dropdownCache[dropdownName]) {
        // Filter the apiResponse based on the dropdownName and store the result in the cache
        dropdownCache[dropdownName] = apiResponse?.reduce((acc, item) => {
            if (item.dropdownName === dropdownName) {
                acc.push({
                    value: item.id,  // ID of the dropdown item
                    label: item.name,  // Name of the dropdown item
                });
            }
            return acc;
        }, []);
    }
    // Return the cached dropdown options
    return dropdownCache[dropdownName];
};

/**
 * Creates a mapping of field IDs to their corresponding field objects for efficient lookup.
 * @param {Object} formData - The form data containing sections, rows, and fields.
 * @returns {Object} - A map of field IDs to their corresponding field objects.
 */
const mapFields = (formData) => {
    const fieldMap = {};

    formData.section.forEach((section) => {
        // Handle fields directly under the section
        if (section.fields) {
            section.fields.forEach((field) => {
                fieldMap[field.id] = field;
            });
        }

        // Handle fields inside rowGroups if present
        if (section.rowGroup) {
            section.rowGroup.forEach((group) => {
                group.fields.forEach((field) => {
                    fieldMap[field.id] = field;
                });
            });
        }
    });

    return fieldMap;
};


/**
 * Updates the options of a specific field in the field map.
 * @param {Object} fieldMap - The map of field IDs to field objects.
 * @param {string} fieldId - The ID of the field to update.
 * @param {Array} options - The new options to assign to the field.
 */
const updateFieldUsingMap = (fieldMap, fieldId, options) => {
    if (fieldMap[fieldId]) {
        fieldMap[fieldId].fieldSetting.options = options; // Update the field's options
    }
};

/**
 * Batch update all dropdown fields in the form data based on the provided configurations.
 * @param {Object} formData - The form data containing fields to update.
 * @param {Array} dropdownConfigs - An array of dropdown configuration objects.
 * @param {Array} apiResponse - An array of dropdown response.
 */
export const setAllDropDownFieldsBatch = (formData, dropdownConfigs, apiResponse) => {
    const fieldMap = mapFields(formData); // Create a map for quick field lookups.
    // Iterate over the dropdown configurations to filter options and update fields
    dropdownConfigs.forEach(({ dropdownName, fieldId }) => {
        // Get the filtered dropdown options (cached if already processed)
        const dropdownOptions = filterDropDownList(dropdownName, apiResponse);
        // Update the corresponding field with the new options
        updateFieldUsingMap(fieldMap, fieldId, dropdownOptions);
    });
};

// Prop-Types validation for functions' parameters

filterDropDownList.propTypes = {
    dropdownName: PropTypes.string.isRequired,
    apiResponse: PropTypes.arrayOf(
        PropTypes.shape({
            dropdownName: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
};

mapFields.propTypes = {
    formData: PropTypes.shape({
        section: PropTypes.arrayOf(
            PropTypes.shape({
                rowGroup: PropTypes.arrayOf(
                    PropTypes.shape({
                        fields: PropTypes.arrayOf(
                            PropTypes.shape({
                                id: PropTypes.string.isRequired,
                                fieldSetting: PropTypes.shape({
                                    options: PropTypes.array,
                                }).isRequired,
                            })
                        ).isRequired,
                    })
                ).isRequired,
            })
        ).isRequired,
    }).isRequired,
};

updateFieldUsingMap.propTypes = {
    fieldMap: PropTypes.object.isRequired,
    fieldId: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
};

setAllDropDownFieldsBatch.propTypes = {
    formData: PropTypes.shape({
        section: PropTypes.arrayOf(
            PropTypes.shape({
                rowGroup: PropTypes.arrayOf(
                    PropTypes.shape({
                        fields: PropTypes.arrayOf(
                            PropTypes.shape({
                                id: PropTypes.string.isRequired,
                                fieldSetting: PropTypes.shape({
                                    options: PropTypes.array,
                                }).isRequired,
                            })
                        ).isRequired,
                    })
                ).isRequired,
            })
        ).isRequired,
    }).isRequired,
    dropdownConfigs: PropTypes.arrayOf(
        PropTypes.shape({
            dropdownName: PropTypes.string.isRequired,
            fieldId: PropTypes.string.isRequired,
            apiResponse: PropTypes.array.isRequired,
        })
    ).isRequired,
};
